import _ from 'lodash'

const isEndBefore = (area1, area2) => area1.max <= area2.min
const isStartAfter = (area1, area2) => area1.min >= area2.max

const getAreasInRange = (areas, range) => {
    let i = 0
    while (i < areas.length && isEndBefore(areas[i], range)) {
        i++
    }

    const areasInRange = []
    while (i < areas.length && !isStartAfter(areas[i], range)) {
        areasInRange.push(areas[i])
        i++
    }

    return areasInRange
}

const getRangesBetween = ranges => {
    const rangesBetween = []

    for (let i = 0; i < ranges.length - 1; i++) {
        if (ranges[i].max !== ranges[i + 1].min) {
            rangesBetween.push({min: ranges[i].max, max: ranges[i + 1].min})
        }
    }

    return rangesBetween
}

const isFullyContainedInRange = (range1, range2) => range2.min <= range1.min && range2.max >= range1.max

const getNonCheckedAreasInRange = (areasAlreadyChecked, range) => {
    const areasAlreadyCheckedInRange = getAreasInRange(areasAlreadyChecked, range)
    if (areasAlreadyCheckedInRange.length === 0) {
        return [range]
    }

    if (isFullyContainedInRange(range, areasAlreadyCheckedInRange[0])) {
        return []
    }

    const areasToNotCheck = [...areasAlreadyCheckedInRange]
    if (areasAlreadyCheckedInRange[0].min > range.min) {
        areasToNotCheck.unshift({min: areasAlreadyCheckedInRange[0].min - 1, max: areasAlreadyCheckedInRange[0].min})
        areasToNotCheck.unshift({min: range.min, max: range.min})
    }

    if (areasAlreadyCheckedInRange[areasAlreadyCheckedInRange.length - 1].max < range.max) {
        areasToNotCheck.push({min: areasAlreadyCheckedInRange[areasAlreadyCheckedInRange.length - 1].max, max: areasAlreadyCheckedInRange[areasAlreadyCheckedInRange.length - 1].max + 1})
        areasToNotCheck.push({min: range.max, max: range.max})
    }

    return getRangesBetween(areasToNotCheck)
}

const getLayoutsInRangesToAdd = (checkedRanges, rangeToCheck, newRangeLayout) => {
    if (checkedRanges.length === 0) {
        return [{breakpointRange: {min: rangeToCheck.min, max: rangeToCheck.max}, layout: newRangeLayout}]
    }

    const nonCheckedAreas = getNonCheckedAreasInRange(checkedRanges, rangeToCheck)

    return nonCheckedAreas.map(area => ({
        breakpointRange: area,
        layout: newRangeLayout
    }))
}

const toCloseRange = layoutInRange => ({
    ...layoutInRange,
    breakpointRange: {...layoutInRange.breakpointRange, min: layoutInRange.breakpointRange.min || 0, max: layoutInRange.breakpointRange.max || Number.MAX_SAFE_INTEGER},
    layout: layoutInRange.layout
})
const layoutsToCloseRanges = layoutInRanges => layoutInRanges.map(toCloseRange)

const toOpenRange = layoutInRange => {
    const openLayoutInRange = {
        layout: layoutInRange.layout,
        breakpointRange: {...layoutInRange.breakpointRange}
    }

    if (openLayoutInRange.breakpointRange.min === 0) {
        delete openLayoutInRange.breakpointRange.min
    }

    if (openLayoutInRange.breakpointRange.max === Number.MAX_SAFE_INTEGER) {
        delete openLayoutInRange.breakpointRange.max
    }

    return openLayoutInRange
}

const toOpenRanges = layoutInRanges => layoutInRanges.map(toOpenRange)

const addCheckedArea = (checkedAreas, areaToMarkAsChecked) => {
    let i = 0
    while (i < checkedAreas.length && isEndBefore(checkedAreas[i], areaToMarkAsChecked)) {
        i++
    }

    const lastIndexBefore = i

    const rangesToMerge = []
    while (i < checkedAreas.length && !isStartAfter(checkedAreas[i], areaToMarkAsChecked)) {
        rangesToMerge.push({...checkedAreas[i]})
        i++
    }

    const firstIndexAfterMergeArea = i

    const mergedArea = rangesToMerge.length === 0 ?
        areaToMarkAsChecked :
        {
            min: Math.min(rangesToMerge[0].min, areaToMarkAsChecked.min),
            max: Math.max(rangesToMerge[rangesToMerge.length - 1].max, areaToMarkAsChecked.max)
        }

    return [
        ...checkedAreas.slice(0, lastIndexBefore),
        mergedArea,
        ...checkedAreas.slice(firstIndexAfterMergeArea)
    ]
}

const addOrMergeToLast = (layoutInRanges, layoutInRangeToAdd) => {
    const lastAddedArea = layoutInRanges[layoutInRanges.length - 1]
    const shouldAddToLast = lastAddedArea &&
        (lastAddedArea.breakpointRange.max + 1 === layoutInRangeToAdd.breakpointRange.min || lastAddedArea.breakpointRange.max === layoutInRangeToAdd.breakpointRange.min) &&
        _.isEqual(layoutInRangeToAdd.layout, lastAddedArea.layout)

    if (shouldAddToLast) {
        layoutInRanges.splice(layoutInRanges.length - 1, 1, {
            layout: layoutInRangeToAdd.layout,
            breakpointRange: {min: lastAddedArea.breakpointRange.min, max: layoutInRangeToAdd.breakpointRange.max}
        })
    } else {
        layoutInRanges.push(layoutInRangeToAdd)
    }
}

const mergeLayoutsInRanges = (layoutsInRanges1, layoutsInRanges2) => {
    let index1 = 0
    let index2 = 0
    const newMarkedAres = []

    while (index1 < layoutsInRanges1.length && index2 < layoutsInRanges2.length) {
        if (layoutsInRanges1[index1].breakpointRange.min < layoutsInRanges2[index2].breakpointRange.min) {
            addOrMergeToLast(newMarkedAres, layoutsInRanges1[index1])
            index1++
        } else {
            addOrMergeToLast(newMarkedAres, layoutsInRanges2[index2])
            index2++
        }
    }

    while (index1 < layoutsInRanges1.length) {
        addOrMergeToLast(newMarkedAres, layoutsInRanges1[index1])
        index1++
    }

    while (index2 < layoutsInRanges2.length) {
        addOrMergeToLast(newMarkedAres, layoutsInRanges2[index2])
        index2++
    }

    return newMarkedAres
}

const getSingleLayoutDataValueByType = (layoutInRange, type) => _.find(layoutInRange.layout, {type})
const getSingleLayoutDataKeyByType = (layoutInRange, type) => _.findKey(layoutInRange.layout, {type})

const omitLayoutKeys = (layoutInRange, propKeys) => {
    const newLayout = {...layoutInRange.layout}
    _.forEach(propKeys, propKey => delete newLayout[propKey])

    return {
        ...layoutInRange,
        layout: newLayout
    }
}

const omitSingleLayoutKeys = (layoutInRange, layoutDataKey, propKeys) => {
    const newLayoutData = {...layoutInRange.layout[layoutDataKey]}
    _.forEach(propKeys, propKey => delete newLayoutData[propKey])

    const newLayout = {
        ...layoutInRange.layout,
        [layoutDataKey]: newLayoutData
    }

    return {
        ...layoutInRange,
        layout: newLayout
    }
}

const isOldLayoutPredicateTruthy = (layoutInRange, layoutType, layoutPropKey, predicate) => layoutInRange.layout.type === layoutType && predicate(layoutInRange.layout[layoutPropKey])

const isSingleLayoutPredicateTruthy = (layoutInRange, layoutType, layoutPropKey, predicate) => layoutInRange.layout.type === 'SingleLayoutData' && predicate(_.get(getSingleLayoutDataValueByType(layoutInRange, layoutType), layoutPropKey))

function getLayoutInRangesToCheck(rootCompLayouts, layoutType, breakpointsOrder) {
    const layoutsRangesByType = rootCompLayouts.filter(layoutInRange => layoutInRange.layout.type === layoutType)
    const singleLayoutDataByType = rootCompLayouts.filter(layoutInRange => layoutInRange.layout.type === 'SingleLayoutData' && getSingleLayoutDataValueByType(layoutInRange, layoutType))

    const singleLayoutDataByTypeRange = _.map(singleLayoutDataByType, layoutInRange => ({
            ...layoutInRange,
            layout: getSingleLayoutDataValueByType(layoutInRange, layoutType)
        }))

    const layoutInRangesToCheck = layoutsToCloseRanges(_.concat(singleLayoutDataByTypeRange, layoutsRangesByType))
    const layoutsByBreakpointId = _.keyBy(layoutInRangesToCheck, 'breakpointId')
    return _(['']).concat(breakpointsOrder).map(breakpointId => layoutsByBreakpointId[breakpointId]).compact().value()
}

export const extractLayoutToNonCoveredRanges = (layoutType, layoutPropKey, predicate, relatedLayoutPropKeys = []) => (rootCompLayouts, breakpointsOrder) => {
    const layoutInRangesToCheck = getLayoutInRangesToCheck(rootCompLayouts, layoutType, breakpointsOrder)
    let layoutsInRangesToAdd = []
    let checkedAreas = []

    layoutInRangesToCheck.reverse().forEach(layoutInRange => {
        if (predicate(layoutInRange.layout[layoutPropKey])) {
            const newRangeLayout = {
                type: layoutType,
                [layoutPropKey]: layoutInRange.layout[layoutPropKey]
            }
            _.forEach(relatedLayoutPropKeys, relatedLayoutPropKey => {
                if (layoutInRange.layout[relatedLayoutPropKey]) {
                    newRangeLayout[relatedLayoutPropKey] = layoutInRange.layout[relatedLayoutPropKey]
                }
            })

            const newLayoutsInRangesToAdd = getLayoutsInRangesToAdd(checkedAreas, layoutInRange.breakpointRange, newRangeLayout)

            layoutsInRangesToAdd = mergeLayoutsInRanges(layoutsInRangesToAdd, newLayoutsInRangesToAdd)
        }

        if (!_.isUndefined(layoutInRange.layout[layoutPropKey])) {
            checkedAreas = addCheckedArea(checkedAreas, layoutInRange.breakpointRange)
        }
    })

    const layouts = []
     _.forEach(rootCompLayouts, layoutInRange => {
            if (isOldLayoutPredicateTruthy(layoutInRange, layoutType, layoutPropKey, predicate)) {
                layouts.push(omitLayoutKeys(layoutInRange, _.concat(relatedLayoutPropKeys, [layoutPropKey])))
            } else if (layoutInRange.layout.type === 'SingleLayoutData') {
                const layoutDataKey = getSingleLayoutDataKeyByType(layoutInRange, layoutType)
                const singleLayoutAfterOmit = isSingleLayoutPredicateTruthy(layoutInRange, layoutType, layoutPropKey, predicate) ?
                    omitSingleLayoutKeys(layoutInRange, layoutDataKey, _.concat(relatedLayoutPropKeys, [layoutPropKey])) :
                    layoutInRange
                _.forEach(['componentLayout', 'itemLayout', 'containerLayout'], layoutKey => {
                    if (!_.isEmpty(singleLayoutAfterOmit.layout[layoutKey])) {
                        layouts.push({...singleLayoutAfterOmit, layout: singleLayoutAfterOmit.layout[layoutKey]})
                    }
                })
            } else {
                layouts.push(layoutInRange)
            }
        })

    return layouts.concat(toOpenRanges(layoutsInRangesToAdd))
}
